import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { WishlistItem } from '../services/firebaseConfig';
// Dynamic import to avoid initialization order issues

interface WishlistContextType {
  wishlistItems: WishlistItem[];
  wishlistCount: number;
  isLoading: boolean;
  isInWishlist: (productId: string) => boolean;
  addToWishlist: (productId: string, productData: { name: string; image: string; price: number }) => Promise<void>;
  removeFromWishlist: (productId: string) => Promise<void>;
  clearWishlist: () => Promise<void>;
  refreshWishlist: () => Promise<void>;
}

const WishlistContext = createContext<WishlistContextType | undefined>(undefined);

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (context === undefined) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};

interface WishlistProviderProps {
  children: ReactNode;
}

export const WishlistProvider: React.FC<WishlistProviderProps> = ({ children }) => {
  const { currentUser } = useAuth();
  const [wishlistItems, setWishlistItems] = useState<WishlistItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const wishlistCount = wishlistItems.length;

  const isInWishlist = useCallback((productId: string): boolean => {
    return wishlistItems.some(item => item.productId === productId);
  }, [wishlistItems]);

  const refreshWishlist = useCallback(async () => {
    if (!currentUser?.uid) {
      setWishlistItems([]);
      return;
    }

    try {
      setIsLoading(true);
      const { wishlistService } = await import('../services/firebaseConfig');
      const items = await wishlistService.getUserWishlist(currentUser.uid);
      setWishlistItems(items);
    } catch (error) {
      setWishlistItems([]);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser?.uid]);

  const addToWishlist = useCallback(async (productId: string, productData: { name: string; image: string; price: number }) => {
    if (!currentUser?.uid) {
      throw new Error('User not authenticated');
    }

    // Check if already in wishlist to prevent duplicates
    if (isInWishlist(productId)) {
      return; // Already in wishlist, no need to add
    }

    // Immediate optimistic update
    const newItem: WishlistItem = {
      id: `temp_${Date.now()}`, // Temporary ID, will be replaced after Firestore add
      productId,
      isFavorite: true,
      addedAt: new Date() as any
    };
    
    // Store previous state for rollback
    const previousItems = [...wishlistItems];
    
    // Update UI immediately
    setWishlistItems(prev => [newItem, ...prev]);

    // Firestore call in background without blocking UI
    try {
      const { wishlistService } = await import('../services/firebaseConfig');
      await wishlistService.addToWishlist(currentUser.uid, productId, productData);
      
      // Refresh wishlist to get the real Firestore document with correct ID and timestamp
      // This ensures state is in sync with Firestore
      await refreshWishlist();
    } catch (error) {
      // Rollback on error
      setWishlistItems(previousItems);
      throw error; // Re-throw to allow caller to handle
    }
  }, [currentUser?.uid, wishlistItems, isInWishlist, refreshWishlist]);

  const removeFromWishlist = useCallback(async (productId: string) => {
    if (!currentUser?.uid) {
      throw new Error('User not authenticated');
    }

    // Store previous state for rollback
    const previousItems = [...wishlistItems];
    
    // Update UI immediately
    setWishlistItems(prev => prev.filter(item => item.productId !== productId));

    // Firestore call in background without blocking UI
    (async () => {
      try {
        const { wishlistService } = await import('../services/firebaseConfig');
        await wishlistService.removeFromWishlist(currentUser.uid, productId);
      } catch (error) {
        // Rollback on error
        setWishlistItems(previousItems);
      }
    })();
  }, [currentUser?.uid, wishlistItems]);

  const clearWishlist = useCallback(async () => {
    if (!currentUser?.uid) {
      throw new Error('User not authenticated');
    }

    // Store previous state for rollback
    const previousItems = [...wishlistItems];
    
    // Update UI immediately
    setWishlistItems([]);

    // Firestore call in background without blocking UI
    (async () => {
      try {
        const { wishlistService } = await import('../services/firebaseConfig');
        await wishlistService.clearWishlist(currentUser.uid);
      } catch (error) {
        // Rollback on error
        setWishlistItems(previousItems);
      }
    })();
  }, [currentUser?.uid, wishlistItems]);

  // Refresh wishlist when user changes
  useEffect(() => {
    refreshWishlist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid]); // refreshWishlist is stable (memoized with useCallback), no need to include it

  const value: WishlistContextType = useMemo(() => ({
    wishlistItems,
    wishlistCount,
    isLoading,
    isInWishlist,
    addToWishlist,
    removeFromWishlist,
    clearWishlist,
    refreshWishlist
  }), [
    wishlistItems,
    wishlistCount,
    isLoading,
    isInWishlist,
    addToWishlist,
    removeFromWishlist,
    clearWishlist,
    refreshWishlist
  ]);

  return (
    <WishlistContext.Provider value={value}>
      {children}
    </WishlistContext.Provider>
  );
};